import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';

import AddchartOutlined from '@mui/icons-material/AddchartOutlined';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import DragIndicator from '@mui/icons-material/DragIndicator';
import Launch from '@mui/icons-material/Launch';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import { Box, Icon, Typography } from '@mui/material';
import Draggable from 'react-draggable';
import { useSearchParams } from 'react-router';

import CustomCheckbox from '@/components/Checkbox';
import { SHOW_SELECTED_URL_PARAM, SPOT_CATALOG_PATH } from '@/constants/constants';
import { Z_INDEX } from '@/constants/z-index';
import type { SymbolKey } from '@/entities/symbol';
import { symbolKey } from '@/entities/symbol';
import { useAddToAppContext } from '@/features/Dashboard/context/AppContext';
import ExportDrawer from '@/features/DataHub/components/ExportDrawer';
import { FACTSHEET_PARAM_NAME } from '@/features/Factsheet/constants';
import {
  clearSelection,
  SELECTOR_MODES
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { useDrawerBackButton } from '@/hooks/useDrawerBackButton';
import useOpenExportSelection, { ExportSelectionSource } from '@/hooks/useOpenExportSelection';
import { closeDrawer, DrawerType, openDrawer } from '@/slices/drawerSlice';

import { SelectorButton } from './components/SelectorButton';
import CreateAppMenu from './CreateAppMenu';
import LimitDialog from './LimitDialog';
import { useSymbolDragBounds } from './useSymbolDragBounds';
import useSymbolSelector from './useSymbolSelector';

const SELECTOR_BOX_SHADOW = '0 6px 8px rgba(255,255,255,0.15)';

/**
 * Returns a human-readable label for the current selection.
 * In forward-curve mode, unique curves are counted by code (Period-agnostic).
 * In spot mode, each selected item is counted individually.
 */
export function formatSelectionLabel(symbols: SymbolKey[], selectorMode: SELECTOR_MODES): string {
  let label = '';
  if (selectorMode === SELECTOR_MODES.FORWARD_CURVE) {
    const n = new Set(symbols.map(s => symbolKey(s, true))).size;
    label = `${n} ${n === 1 ? 'curve' : 'curves'}`;
  } else {
    const n = symbols.length;
    label = `${n} ${n === 1 ? 'item' : 'items'}`;
  }
  return `${label} selected`;
}

const SymbolSelector = () => {
  const symbolSelectorState = useAppSelector(state => state.symbolSelector);
  const openDrawerType = useAppSelector(state => state.drawer.openDrawer);
  const { onSubmit } = useAddToAppContext();
  const {
    handleDrag,
    handleClose,
    position,
    navigate,
    isInCatalog,
    isDashboardAppSelector,
    setPosition
  } = useSymbolSelector();

  const dispatch = useAppDispatch();

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const closeFactsheetWindow = () => {
    setUrlSearchParams(searchParams => {
      searchParams.delete(FACTSHEET_PARAM_NAME);
      return searchParams;
    });
  };
  const popperAnchorRef = useRef<HTMLElement | undefined>(undefined);
  const [createAppAnchor, setCreateAppAnchor] = useState<HTMLElement | null>(null);

  const clearSelectorSelection = () => {
    if (urlSearchParams.get(SHOW_SELECTED_URL_PARAM)) {
      const newSearchParams = new URLSearchParams(urlSearchParams);
      newSearchParams.set(SHOW_SELECTED_URL_PARAM, 'false');
      setUrlSearchParams(newSearchParams);
    }
    dispatch(clearSelection());
    setPosition(undefined);
  };

  const { dragBounds, dragging, setDragging } = useSymbolDragBounds(popperAnchorRef);
  const openExportSelection = useOpenExportSelection(ExportSelectionSource.SYMBOL_SELECTOR);

  const isExportOpen = openDrawerType === DrawerType.EXPORT;

  const closeDrawerOnly = useCallback(() => {
    dispatch(closeDrawer());
  }, [dispatch]);

  const openExportDrawer = useCallback(() => {
    dispatch(openDrawer(DrawerType.EXPORT));
  }, [dispatch]);

  useDrawerBackButton(isExportOpen, closeDrawerOnly, openExportDrawer);

  return (
    <>
      <LimitDialog
        handleClose={handleClose}
        open={symbolSelectorState.limitReached}
        selectorMode={symbolSelectorState.selectorMode}
      />
      {dragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: Z_INDEX.priorityMid,
            pointerEvents: 'all'
          }}
        />
      )}
      {position && (
        <Draggable
          bounds={dragBounds}
          handle='div.cursor'
          nodeRef={popperAnchorRef as RefObject<HTMLElement | null>}
          position={position}
          onDrag={handleDrag}
          onStart={() => {
            setDragging(true);
          }}
          onStop={() => {
            setDragging(false);
          }}
        >
          <Box
            ref={popperAnchorRef}
            className='scroll--white'
            sx={{
              p: 1.5,
              pr: 1,
              backgroundColor:
                symbolSelectorState.selectorMode === SELECTOR_MODES.FORWARD_CURVE
                  ? 'gx.selectorForwardCurve'
                  : 'primary.main',
              minWidth: 354,
              zIndex: Z_INDEX.priority,
              position: 'absolute',
              boxShadow: SELECTOR_BOX_SHADOW,
              display:
                symbolSelectorState.symbols.length > 0 &&
                !symbolSelectorState.limitReached &&
                !isExportOpen
                  ? 'flex'
                  : 'none',
              flexDirection: 'column'
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                mb: '8px'
              }}
            >
              {symbolSelectorState.selectorMode === SELECTOR_MODES.FORWARD_CURVE
                ? 'Curve Mode'
                : 'Item Mode'}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <Box
                className='cursor'
                style={{ paddingRight: 8 }}
              >
                <Icon sx={{ cursor: 'all-scroll' }}>
                  <DragIndicator />
                </Icon>
              </Box>
              <Box
                sx={{
                  cursor: 'default',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center'
                }}
              >
                <CustomCheckbox
                  checked
                  sx={{ cursor: 'default' }}
                />
                <Box
                  sx={{
                    cursor: 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    px: 1,
                    mr: 1
                  }}
                >
                  <Typography sx={{ fontSize: 12 }}>
                    {formatSelectionLabel(
                      symbolSelectorState.symbols,
                      symbolSelectorState.selectorMode
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <SelectorButton
                  onClick={() => {
                    clearSelectorSelection();
                  }}
                >
                  <DeleteOutlined />
                  <Typography variant='caption'>Clear selection</Typography>
                </SelectorButton>

                {!isDashboardAppSelector && (
                  <>
                    <SelectorButton
                      disabled={isInCatalog}
                      onClick={() => {
                        closeFactsheetWindow();
                        navigate(`/catalog/${SPOT_CATALOG_PATH}?${SHOW_SELECTED_URL_PARAM}=true`);
                      }}
                    >
                      <ListAltOutlined />
                      <Typography variant='caption'>View in Catalog</Typography>
                    </SelectorButton>
                    <SelectorButton
                      onClick={() => {
                        closeFactsheetWindow();
                        openExportSelection();
                      }}
                    >
                      <Launch />
                      <Typography variant='caption'>Export selection</Typography>
                    </SelectorButton>
                    <SelectorButton
                      sx={{ width: 146, justifyContent: 'space-between' }}
                      onClick={e => {
                        setCreateAppAnchor(prev => (prev ? null : e.currentTarget));
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <AddchartOutlined />
                        <Typography variant='caption'>Create app</Typography>
                      </Box>
                      <ArrowDropDown />
                    </SelectorButton>
                    <CreateAppMenu
                      containerEl={popperAnchorRef.current}
                      open={Boolean(createAppAnchor)}
                      selectorMode={symbolSelectorState.selectorMode}
                      onClose={() => {
                        setCreateAppAnchor(null);
                      }}
                    />
                  </>
                )}

                {isDashboardAppSelector && (
                  <SelectorButton
                    onClick={() => {
                      if (onSubmit) {
                        onSubmit(symbolSelectorState.symbols);
                      }
                    }}
                  >
                    <ListAltOutlined />
                    <Typography variant='caption'>Add to app</Typography>
                  </SelectorButton>
                )}
              </Box>
            </Box>
          </Box>
        </Draggable>
      )}
      <ExportDrawer
        open={isExportOpen}
        onClose={closeDrawerOnly}
      />
    </>
  );
};

export default SymbolSelector;
